import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Euro, FileText, Megaphone, Download, Trash2, Undo2,
  Loader2, BarChart3, Calendar, PhoneCall, ShoppingBag,
} from 'lucide-react';
import Navigation from '../components/Navigation';
import CallUsageChart, { formatSeconds } from '../components/CallUsageChart';
import GlobalAccountingPanel from '../components/GlobalAccountingPanel';
import ApiUsagePanel from '../components/ApiUsagePanel';
import ExpensesPanel from '../components/admin/ExpensesPanel';

const PERIODS = [
  { id: 'day',   label: 'Jour' },
  { id: 'week',  label: 'Semaine' },
  { id: 'month', label: 'Mois' },
  { id: 'year', label: 'Année' },
];
const SOURCES = [
  { id: 'all',    label: 'Tout' },
  { id: 'ad',     label: 'Publicités' },
  { id: 'edepot', label: 'e-Dépôts' },
];

const CALL_PERIODS = [
  { id: 'day',   label: 'Jour',    last_n: 30 },
  { id: 'week',  label: 'Semaine', last_n: 12 },
  { id: 'month', label: 'Mois',    last_n: 12 },
];

const formatEur = (n) => `${(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const AdminAccounting = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('month');
  const [source, setSource] = useState('all');
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, source } | null
  const [busy, setBusy] = useState(false);
  const [callPeriod, setCallPeriod] = useState('day');
  const [callUsage, setCallUsage] = useState(null);
  const [loadingCallUsage, setLoadingCallUsage] = useState(false);

  const loadSummary = async (p = period) => {
    try {
      const res = await axios.get(`/api/admin/accounting/summary?period=${p}&last_n=12`);
      setSummary(res.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Accès admin requis");
        navigate('/admin');
      } else { toast.error("Erreur de chargement des totaux"); }
    }
  };
  const loadTransactions = async (s = source) => {
    try {
      const res = await axios.get(`/api/admin/accounting/transactions?source=${s}&limit=200`);
      setTransactions(res.data.transactions || []);
    } catch (_) { toast.error("Erreur de chargement des transactions"); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadSummary(period), loadTransactions(source)]);
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  useEffect(() => { loadSummary(period); }, [period]); // eslint-disable-line
  useEffect(() => { loadTransactions(source); }, [source]); // eslint-disable-line

  const loadCallUsage = async (p = callPeriod) => {
    const cfg = CALL_PERIODS.find(x => x.id === p) || CALL_PERIODS[0];
    setLoadingCallUsage(true);
    try {
      const res = await axios.get(`/api/admin/accounting/call-usage?period=${cfg.id}&last_n=${cfg.last_n}`);
      setCallUsage(res.data);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error("Erreur chargement temps d'appel");
    } finally { setLoadingCallUsage(false); }
  };
  useEffect(() => { loadCallUsage(callPeriod); }, [callPeriod]); // eslint-disable-line

  const handleDelete = async (txn) => {
    setBusy(true);
    try {
      await axios.delete(`/api/admin/accounting/transactions/${txn.source}/${txn.id}`);
      toast.success("Transaction supprimée de la comptabilité");
      setConfirmDelete(null);
      await Promise.all([loadSummary(period), loadTransactions(source)]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
    finally { setBusy(false); }
  };
  const handleRestore = async (txn) => {
    setBusy(true);
    try {
      await axios.post(`/api/admin/accounting/transactions/${txn.source}/${txn.id}/restore`);
      toast.success("Transaction restaurée");
      await Promise.all([loadSummary(period), loadTransactions(source)]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Restauration impossible"); }
    finally { setBusy(false); }
  };

  const exportCSV = async () => {
    try {
      const url = `${axios.defaults.baseURL || ''}/api/admin/accounting/export.csv?source=${source}`;
      window.open(url, '_blank');
    } catch (_) { toast.error("Export impossible"); }
  };

  const maxBucket = useMemo(() => {
    if (!summary?.buckets?.length) return 1;
    return Math.max(...summary.buckets.map(b => b.total_eur), 1);
  }, [summary]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-black to-fuchsia-900 text-white pb-24" data-testid="admin-accounting-page">
      <div className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-white/10 px-4 py-4" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <div className="flex items-center gap-3 max-w-3xl mx-auto">
          <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="acc-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Euro className="w-5 h-5 text-emerald-400" /> Comptabilité
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">Revenus Publicités + e-Dépôts (Stripe)</p>
          </div>
          <button onClick={exportCSV} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-full text-xs font-semibold flex items-center gap-1.5" data-testid="acc-export-csv">
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-5">
        {/* v49.6 — Unified global accounting (all DS revenue sources) */}
        <GlobalAccountingPanel />

        {/* v49.7 — API usage & quota forecasts (Resend / Airalo / Stripe / Agora / TMDB / Emergent LLM) */}
        <ApiUsagePanel />

        {/* v50.16 — Dépenses & abonnements (moved from Destine IA) */}
        <ExpensesPanel />

        {/* Period switcher */}
        <div className="flex items-center gap-2" data-testid="acc-period-switch">
          <Calendar className="w-4 h-4 text-gray-400" />
          {PERIODS.map(p => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${period === p.id ? 'bg-fuchsia-600 border-fuchsia-500' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
              data-testid={`acc-period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="acc-kpi-cards">
          <KpiCard label="Total" value={summary?.totals?.total_eur} count={summary?.totals?.count_total} icon={<Euro className="w-4 h-4" />} color="from-emerald-600/30 to-emerald-900/10 border-emerald-500/30" />
          <KpiCard label="Publicités" value={summary?.totals?.ads_eur} count={summary?.totals?.count_ads} icon={<Megaphone className="w-4 h-4" />} color="from-violet-600/30 to-violet-900/10 border-violet-500/30" />
          <KpiCard label="e-Dépôts" value={summary?.totals?.edepots_eur} count={summary?.totals?.count_edepots} icon={<FileText className="w-4 h-4" />} color="from-fuchsia-600/30 to-fuchsia-900/10 border-fuchsia-500/30" />
          <KpiCard label="Commission MyShop (5%)" value={summary?.totals?.shop_commission_eur} count={summary?.totals?.count_shop_orders} icon={<ShoppingBag className="w-4 h-4" />} color="from-amber-600/30 to-amber-900/10 border-amber-500/30" testid="acc-kpi-shop" />
          <KpiCard label="Abonnements Premium" value={summary?.totals?.subscriptions_eur} count={summary?.totals?.count_subscriptions} icon={<FileText className="w-4 h-4" />} color="from-yellow-600/30 to-yellow-900/10 border-yellow-500/30" testid="acc-kpi-subscriptions" />
          <KpiCard label="Premium Ads (Ads le commercial)" value={summary?.totals?.ads_premium_eur} count={summary?.totals?.count_ads_premium} icon={<FileText className="w-4 h-4" />} color="from-fuchsia-600/30 to-fuchsia-900/10 border-fuchsia-500/30" testid="acc-kpi-ads-premium" />
        </div>

        {/* Buckets */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-fuchsia-300" /> Détail par {PERIODS.find(p => p.id === period)?.label.toLowerCase()}
          </h2>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : summary?.buckets?.length ? (
            <div className="space-y-2.5" data-testid="acc-buckets">
              {summary.buckets.map(b => {
                const pct = Math.round((b.total_eur / maxBucket) * 100);
                return (
                  <div key={b.key} className="space-y-1" data-testid={`acc-bucket-${b.key}`}>
                    <div className="flex items-center justify-between text-xs text-gray-300">
                      <span className="font-mono">{b.key}</span>
                      <span className="font-semibold text-white">{formatEur(b.total_eur)}</span>
                    </div>
                    <div className="h-3 rounded-full bg-white/5 overflow-hidden flex">
                      <div className="h-full bg-violet-500" style={{ width: `${(b.ads_eur / Math.max(b.total_eur, 0.01)) * pct}%` }} title={`Pub: ${formatEur(b.ads_eur)}`} />
                      <div className="h-full bg-fuchsia-500" style={{ width: `${(b.edepots_eur / Math.max(b.total_eur, 0.01)) * pct}%` }} title={`e-Dépôts: ${formatEur(b.edepots_eur)}`} />
                      <div className="h-full bg-amber-500" style={{ width: `${((b.shop_commission_eur || 0) / Math.max(b.total_eur, 0.01)) * pct}%` }} title={`Commission MyShop: ${formatEur(b.shop_commission_eur)}`} />
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-gray-400 flex-wrap">
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" /> {formatEur(b.ads_eur)}</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-fuchsia-500" /> {formatEur(b.edepots_eur)}</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> {formatEur(b.shop_commission_eur)}</span>
                      <span className="ml-auto">{b.count} txn</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">Aucun revenu sur la période.</p>
          )}
        </div>

        {/* Call-time usage (Agora cost forecasting) */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="admin-call-usage-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <PhoneCall className="w-4 h-4 text-cyan-300" /> Temps d'appel global (Agora)
            </h2>
            <div className="flex gap-1" data-testid="admin-call-period-switch">
              {CALL_PERIODS.map(p => (
                <button
                  key={p.id}
                  onClick={() => setCallPeriod(p.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${callPeriod === p.id ? 'bg-cyan-600 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                  data-testid={`admin-call-period-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {loadingCallUsage ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : callUsage ? (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3" data-testid="admin-call-kpi">
                <div className="rounded-xl border bg-gradient-to-br from-cyan-600/20 to-cyan-900/10 border-cyan-500/30 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-white/70 mb-0.5">Total</p>
                  <p className="text-base font-bold" data-testid="admin-call-total">{formatSeconds(callUsage.total_seconds || 0)}</p>
                  <p className="text-[10px] text-white/60 mt-0.5">{callUsage.total_hours || 0} h</p>
                </div>
                <div className="rounded-xl border bg-gradient-to-br from-violet-600/20 to-violet-900/10 border-violet-500/30 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-white/70 mb-0.5">Appels</p>
                  <p className="text-base font-bold">{callUsage.total_calls || 0}</p>
                  <p className="text-[10px] text-white/60 mt-0.5">sessions facturées</p>
                </div>
                <div className="rounded-xl border bg-gradient-to-br from-fuchsia-600/20 to-fuchsia-900/10 border-fuchsia-500/30 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-white/70 mb-0.5">Utilisateurs</p>
                  <p className="text-base font-bold">{callUsage.total_unique_users || 0}</p>
                  <p className="text-[10px] text-white/60 mt-0.5">uniques (deux côtés)</p>
                </div>
              </div>
              <CallUsageChart
                data={(callUsage.series || []).map(d => ({
                  label: callPeriod === 'day' ? d.bucket.slice(5) : d.bucket,
                  value: d.seconds,
                  sublabel: `${d.calls_count} appel(s) · ${d.unique_users} u.`,
                }))}
                height={140}
                barColor="fill-cyan-500"
                testid="admin-call-usage-chart"
              />
              <p className="text-[11px] text-gray-400 mt-2">
                Astuce coût Agora : ~0,99 USD / 1 000 min audio · 3,99 USD / 1 000 min vidéo HD.
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">Aucune donnée.</p>
          )}
        </div>

        {/* Source switch + transactions */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-300">Transactions</h2>
            <div className="flex gap-1" data-testid="acc-source-switch">
              {SOURCES.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSource(s.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${source === s.id ? 'bg-fuchsia-600 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                  data-testid={`acc-source-${s.id}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          {transactions.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">Aucune transaction.</p>
          ) : (
            <ul className="divide-y divide-white/5" data-testid="acc-txn-list">
              {transactions.map(txn => (
                <li key={`${txn.source}-${txn.id}`} className="py-3 flex items-center gap-3" data-testid={`acc-txn-${txn.id}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${txn.source === 'ad' ? 'bg-violet-500/20 text-violet-300' : 'bg-fuchsia-500/20 text-fuchsia-300'}`}>
                    {txn.source === 'ad' ? <Megaphone className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{txn.source === 'ad' ? 'Publicité payée' : (txn.meta?.title || 'e-Dépôt payé')}</p>
                    <p className="text-[11px] text-gray-400 truncate">
                      {new Date(txn.date).toLocaleString('fr-FR')} · {txn.user_id?.slice(0, 8) || '—'}
                    </p>
                  </div>
                  <span className="text-sm font-bold text-emerald-300 shrink-0">{formatEur(txn.amount_eur)}</span>
                  <button
                    onClick={() => setConfirmDelete(txn)}
                    className="p-2 -mr-1 text-gray-400 hover:text-red-400 hover:bg-white/5 rounded-full"
                    title="Supprimer de la comptabilité"
                    data-testid={`acc-txn-delete-${txn.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex items-end sm:items-center justify-center" onClick={() => setConfirmDelete(null)}>
          <div className="bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 border border-white/10" onClick={e => e.stopPropagation()} data-testid="acc-delete-modal">
            <h3 className="text-lg font-bold mb-2">Supprimer cette transaction ?</h3>
            <p className="text-sm text-gray-300 mb-4">
              La transaction sera retirée du résumé comptable, mais le paiement Stripe et le reçu utilisateur restent intacts. Vous pourrez la restaurer à tout moment.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold" data-testid="acc-delete-cancel">
                Annuler
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={busy}
                className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="acc-delete-confirm"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Supprimer
              </button>
              <button
                onClick={() => handleRestore(confirmDelete)}
                disabled={busy}
                className="px-3 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
                title="Annuler une suppression précédente"
                data-testid="acc-restore-action"
              >
                <Undo2 className="w-4 h-4" /> Restaurer
              </button>
            </div>
          </div>
        </div>
      )}

      <Navigation />
    </div>
  );
};

const KpiCard = ({ label, value, count, icon, color, testid }) => (
  <div className={`rounded-2xl border bg-gradient-to-br ${color} p-3.5`} data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[11px] text-white/70 mb-1">{icon}<span className="uppercase tracking-wider">{label}</span></div>
    <p className="text-base sm:text-lg font-bold leading-tight">{formatEur(value)}</p>
    <p className="text-[10px] text-white/60 mt-0.5">{count || 0} transaction{(count || 0) > 1 ? 's' : ''}</p>
  </div>
);

export default AdminAccounting;
